import * as PIXI from 'pixi.js';
import { B2dEditor } from '../b2Editor/B2dEditor';
import { game } from '../Game';
import { BACKGROUND_TEXTURE_SIZE, loadBackgroundTextures } from './backgroundAssets';

let currentBackground = '';
let requestedBackground = '';
let backgroundRequestId = 0;
let backgroundLoadPromise = null;

let bg = null;
let mg = null;
let fg = null;

const destroyBackgroundDisplay = () => {
	for (const displayObject of [bg, mg, fg]) {
		displayObject?.destroy({
			children: true,
			texture: false,
			textureSource: false,
		});
	}

	bg = null;
	mg = null;
	fg = null;
	currentBackground = '';
};

const createTiledLayer = texture => {
	const layer = new PIXI.Container();
	layer.innerSprite = new PIXI.Container();
	layer.addChild(layer.innerSprite);

	for (let i = 0; i < 3; i++) {
		const image = new PIXI.Sprite(texture);
		image.anchor.set(0.5);
		image.x = BACKGROUND_TEXTURE_SIZE * [0, 1, -1][i];
		layer.innerSprite.addChild(image);
	}

	return layer;
};

export const setBackground = backgroundName => {
	backgroundName = backgroundName || '';
	if (requestedBackground === backgroundName) {
		return backgroundLoadPromise || Promise.resolve();
	}

	requestedBackground = backgroundName;
	const requestId = ++backgroundRequestId;

	destroyBackgroundDisplay();

	if (!backgroundName) {
		backgroundLoadPromise = null;
		return Promise.resolve();
	}

	backgroundLoadPromise = loadBackgroundTextures(PIXI.Assets, backgroundName)
		.then(([backgroundTexture, middleTexture, foregroundTexture]) => {
			// A slower previous request must never replace a newer background.
			if (requestId !== backgroundRequestId || requestedBackground !== backgroundName) return;

			const backgroundContainer = game.editor?.background;
			if (!backgroundContainer || backgroundContainer.destroyed) {
				requestedBackground = '';
				return;
			}

			bg = new PIXI.Sprite(backgroundTexture);
			bg.anchor.set(0.5);
			mg = createTiledLayer(middleTexture);
			fg = createTiledLayer(foregroundTexture);

			backgroundContainer.addChild(bg, mg, fg);
			currentBackground = backgroundName;
		})
		.catch(error => {
			if (requestId !== backgroundRequestId) return;

			requestedBackground = '';
			console.error(`Failed to load background "${backgroundName}".`, error);
		})
		.finally(() => {
			if (requestId === backgroundRequestId) backgroundLoadPromise = null;
		});

	return backgroundLoadPromise;
};

export const updateBackground = () => {
	if(!currentBackground || !bg || !mg || !fg) return;

	// BG
	const camera = B2dEditor.container.camera || B2dEditor.container;

	const targetWidth = window.innerWidth / camera.scale.x;
	const targetHeight = window.innerHeight / camera.scale.x;

	let targetScale = targetWidth / (bg.width / bg.scale.x);

	if((bg.height / bg.scale.y) * targetScale < targetHeight){
		targetScale = targetHeight / (bg.height / bg.scale.y);
	}

	bg.scale.set(targetScale);

	const targetX = (-camera.x + window.innerWidth / 2) / camera.scale.x;
	const targetY = (-camera.y + window.innerHeight / 2) / camera.scale.x;

	bg.position.set(targetX, targetY);

	// MG
	const minZoom = 0.01;
	const maxZoom = 10;
	const zoomDiff = maxZoom - minZoom;
	const zoomProgress = (camera.scale.x - minZoom) / zoomDiff;

	const invCameraScale = 1 / camera.scale.x;

	const mgMinZoom = 1.7;
	const mgMaxZoom = 3;
	const mgZoom = mgMinZoom + (mgMaxZoom - mgMinZoom) * zoomProgress;

	const mgParralax = 0.99 + (0.01 * zoomProgress);

	mg.scale.set(invCameraScale);
	mg.innerSprite.scale.set(mgZoom);
	mg.position.set(targetX * mgParralax, targetY * mgParralax);

	// FG
	const fgMinZoom = 1.7;
	const fgMaxZoom = 5;
	const fgZoom = fgMinZoom + (fgMaxZoom - fgMinZoom) * zoomProgress;

	const fgParralax = 0.98 + (0.02 * zoomProgress);

	fg.scale.set(invCameraScale);
	fg.innerSprite.scale.set(fgZoom);
	fg.position.set(targetX * fgParralax, targetY * fgParralax);
}
